import { useToCanvas } from '@hugocxl/react-to-image'
import { Box } from '@mui/material'
import type { Feature, GeoJSON as GeoJSONType } from 'geojson'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useEffect, useState } from 'react'
import { FeatureGroup, GeoJSON, LayerGroup, MapContainer, TileLayer } from 'react-leaflet'
import { pickChildrenByType } from '../../utils/childrenPickers'
import { propertiesPopupContent } from '../../utils/popupContent'
import { useCustomSnackbarError } from '../../utils/Snackbar/useCustomSnackbarError'
import { AddPolygonButton } from './AddPolygonButton'
import { BaseLayers } from './BaseLayers'
import { CancelEditButton } from './CancelEditButton'
import { DeleteMultiPolygonButton } from './DeleteMultiPolygonButton'
import { EditModeChip } from './EditModeChip'
import { FloatingActionButtons } from './FloatingActionButtons'
import { GeomanControls } from './GeomanControls'
import { LayersControl } from './LayersControl'
import { Legend, type LegendProps } from './Legend'
import { MapProvider } from './MapContext'
import { MapLoading } from './MapLoading'
import { MultiPolygonDeleteMarkers } from './MultiPolygonDeleteMarkers'
import { getColor } from './utils'

const defaultPopupContent = (feature: Feature): string => propertiesPopupContent(feature.properties)

interface LayerProps {
  layer: GeoJSONType
  layerStyles?: (feature: Feature) => Record<string, unknown>
  popupContent?: (feature: Feature) => string
}
const Layer: React.FC<LayerProps> = ({
  layer,
  layerStyles = () => ({}),
  popupContent = defaultPopupContent,
}) => {
  return (
    <FeatureGroup>
      <LayerGroup>
        <GeoJSON
          data={layer}
          style={(feature: Feature) => {
            return {
              color: getColor(feature),
              weight: 1,
              opacity: 1,
              fillOpacity: 0.2,
              ...layerStyles(feature),
            }
          }}
          onEachFeature={(feature, geoLayer) => {
            geoLayer.bindPopup(popupContent(feature))
          }}
        />
      </LayerGroup>
    </FeatureGroup>
  )
}

const DEFAULT_CENTER: L.LatLngTuple = [-35.329970415341656, -65.12262446073629]

interface MapProps {
  perimeter: GeoJSON.Feature
  mapWidth: number
  mapHeight: number | string
  flexDirection?: 'row' | 'column'
  scrollWheelZoom?: boolean
  isImage?: boolean
  isLoading?: boolean
  children: React.ReactNode
}

export const Map: React.FC<MapProps> & {
  Legend: React.FC<LegendProps>
  Layer: React.FC<LayerProps>
  BaseLayers: typeof BaseLayers
  LayersControl: typeof LayersControl
  GeomanControls: typeof GeomanControls
  EditModeChip: typeof EditModeChip
  FloatingActionButtons: typeof FloatingActionButtons
  MultiPolygonDeleteMarkers: typeof MultiPolygonDeleteMarkers
  DeleteMultiPolygonButton: typeof DeleteMultiPolygonButton
  AddPolygonButton: typeof AddPolygonButton
  CancelEditButton: typeof CancelEditButton
} = ({
  perimeter,
  mapWidth,
  mapHeight,
  flexDirection = 'row',
  scrollWheelZoom = false,
  isImage = false,
  isLoading = false,
  children,
}) => {
  const [map, setMap] = useState<L.Map | null>(null)
  const { handleError } = useCustomSnackbarError()
  const [baseTileLoaded, setBaseTileLoaded] = useState(false)
  const [imageState, convert, ref] = useToCanvas()
  const legendDirection = flexDirection === 'column' ? 'row' : 'column'

  useEffect(() => {
    if (isImage && map && baseTileLoaded) {
      convert()
    }
    // convert() from useToCanvas is recreated each render; including it would re-run the
    // canvas snapshot in a loop, so it is intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImage, map, baseTileLoaded])

  useEffect(() => {
    try {
      if (
        map &&
        perimeter?.geometry &&
        'coordinates' in perimeter.geometry &&
        perimeter.geometry.coordinates?.length > 0
      ) {
        const bounds = L.geoJSON(perimeter).getBounds()
        const center = bounds.getCenter()
        const zoom = map.getBoundsZoom(bounds)
        map.setView(center, zoom)
      }
    } catch (error) {
      handleError(error)
    }
  }, [handleError, perimeter, map])

  useEffect(() => {
    if (map) {
      const timeoutId = setTimeout(() => {
        map.invalidateSize()
      }, 100)

      return () => clearTimeout(timeoutId)
    }
    return () => {}
  }, [map, mapWidth, mapHeight])

  if (
    !(
      perimeter?.geometry &&
      'coordinates' in perimeter.geometry &&
      perimeter.geometry.coordinates?.length > 0
    )
  )
    return null

  return (
    <MapProvider
      values={{
        perimeter,
        legendDirection,
        mapWidth,
        map,
        setMap,
      }}
    >
      {isLoading && <MapLoading />}
      <Box sx={{ display: 'flex', gap: '16px', flexDirection }}>
        <div ref={ref} style={{ height: mapHeight, width: mapWidth }}>
          {imageState.data ? (
            <img
              alt="map"
              src={imageState.data.toDataURL('image/png')}
              style={{ height: mapHeight, width: mapWidth }}
            />
          ) : (
            <MapContainer
              key={`${mapWidth}-${mapHeight}`}
              ref={setMap}
              zoomControl
              attributionControl={false}
              center={DEFAULT_CENTER}
              scrollWheelZoom={scrollWheelZoom}
              style={{ height: mapHeight, width: mapWidth }}
              wheelPxPerZoomLevel={60}
              zoom={5}
              zoomDelta={1}
              zoomSnap={0.1}
            >
              {!pickChildrenByType(children, BaseLayers).length &&
                !pickChildrenByType(children, LayersControl).length && (
                  <TileLayer
                    eventHandlers={{
                      load: () => {
                        setTimeout(() => {
                          setBaseTileLoaded(true)
                        }, 1000)
                      },
                    }}
                    maxNativeZoom={19}
                    maxZoom={22}
                    subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                    url="https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                  />
                )}

              <LayerGroup>
                <GeoJSON
                  data={perimeter}
                  style={_ => {
                    return {
                      color: getColor(perimeter),
                      weight: 3,
                      opacity: 1,
                      fillOpacity: 0,
                    }
                  }}
                />
              </LayerGroup>

              {pickChildrenByType(children, BaseLayers)}
              {pickChildrenByType(children, LayersControl)}
              {pickChildrenByType(children, GeomanControls)}
              {pickChildrenByType(children, Layer)}
            </MapContainer>
          )}
          {pickChildrenByType(children, EditModeChip)}
          {pickChildrenByType(children, FloatingActionButtons)}
        </div>
        {pickChildrenByType(children, Legend)}
      </Box>
    </MapProvider>
  )
}

Map.Legend = Legend
Map.Layer = Layer
Map.BaseLayers = BaseLayers
Map.LayersControl = LayersControl
Map.GeomanControls = GeomanControls
Map.EditModeChip = EditModeChip
Map.FloatingActionButtons = FloatingActionButtons
Map.MultiPolygonDeleteMarkers = MultiPolygonDeleteMarkers
Map.DeleteMultiPolygonButton = DeleteMultiPolygonButton
Map.AddPolygonButton = AddPolygonButton
Map.CancelEditButton = CancelEditButton
